import { Directive, ElementRef, Input } from '@angular/core';
import { Router } from '@angular/router';
import 'js-marker-clusterer/src/markerclusterer.js';

import { MapService } from './healthcarefinder-map.service';
import { WebApiModel } from '../../models/web-api.model';
import { GoogleMapLoaderService } from '../../services/google-map-loader-service';
import { RouterHelperService, RouteUrl } from '../../services/router-helper.service';
import { StateService } from '../../services/state.service';

// declare var google: any;
declare let MarkerClusterer;
declare function require(string): string;
const maxZoom = 14;

@Directive({
  selector: '[hcfMarkerExtension]',
  standalone: false,
})
export class MarkerExtensionDirective {
  private _map: google.maps.Map;
  private _markers = [];
  private _locations = [];
  private markerClusterer: any;
  lastSelectedMarker: any;
  lastSelectedlocation: WebApiModel.Location;
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() isPopup: boolean;
  @Input()
  set data(locations: any[]) {
    this._locations = locations;
    this.setupMapMarkers(true);
  }

  constructor(
    private _elementRef: ElementRef,
    private _stateService: StateService,
    private _mapLoaderService: GoogleMapLoaderService,
    private _mapService: MapService,
    private _router: Router,
  ) {}

  setupMapMarkers(resetZoom: boolean) {
    // Remove any previously added markers from the map
    // for (let i = 0; i < this._markers.length; i++) {
    //   this._markers[i].setMap(null);
    // }
    for (const marker of this._markers) {
      marker.setMap(null);
    }

    this._markers = []; // Reset markers each time new data is received.

    if (this._map) {
      this.refershMap(this._map, resetZoom);
    } else {
      const mapContainer = this._elementRef.nativeElement.closest('.map-container');
      this._mapLoaderService
        .createMap(mapContainer)
        .then((map) => {
          this._map = map;
          this.refershMap(map, resetZoom);
        })
        .catch((error) => {}); // eslint-disable-line @typescript-eslint/no-empty-function
    }
  }

  refershMap(map: google.maps.Map, resetZoom: boolean) {
    const mapOptions: google.maps.MapOptions = {
      disableDefaultUI: true,
      streetViewControl: true, // Enable street view control
      zoomControl: true, // Enable zoom control
      scrollwheel: true,
    };
    map.setOptions(mapOptions);

    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    // If no locations then no markers
    if (!this._locations || this._locations.length === 0) {
      this._mapService.setDefaultMap(map);
      return;
    }

    // One info window object reused by many markers, this way only one info window will be open on the map at a time.
    const infoWindow = new google.maps.InfoWindow();

    for (const location of this._locations) {
      const currentLocation: WebApiModel.Location = location;
      const latLng = new google.maps.LatLng(currentLocation.latitude, currentLocation.longitude);
      const icon = this.getIcon(currentLocation, false);
      const isMultiLocation = currentLocation.locationDetails.length > 1;

      const marker = new google.maps.Marker({
        map,
        optimized: false, // Needed for IE11, otherwise markers are not showing properly and zoom is not working well.
        icon: this.getIconImage(icon),
        position: latLng,
        title: isMultiLocation ? currentLocation.address : currentLocation.locationDetails[0].practiceName,
      });

      if (isMultiLocation) {
        const label = {
          text: currentLocation.locationDetails.length.toString(),
          fontFamily: 'Arial',
          color: icon.isLabelBlue ? '#125687' : '#85ac59',
          fontWeight: '600',
        };
        marker.setLabel(label);
      }

      // Add info window content and marker click handler
      marker.addListener('click', () => {
        if (isMultiLocation) {
          infoWindow.close();
          if (!this.isPopup) {
            this._stateService.openPopupMap([currentLocation]);
          }
          this._stateService.toggleMapSidebar(currentLocation);
        } else {
          const infoWindowContent = this.buildInfoWindow(currentLocation);
          infoWindow.setContent(infoWindowContent);
          infoWindow.open(map, marker);
          this._stateService.toggleMapSidebar(null);
        }

        if (this.lastSelectedMarker && this.lastSelectedlocation) {
          this.lastSelectedMarker.setIcon(this.getIconImage(this.getIcon(this.lastSelectedlocation, false)));
        }

        const theIcon = this.getIcon(currentLocation, true);
        marker.setIcon(this.getIconImage(theIcon));

        this.lastSelectedMarker = marker;
        this.lastSelectedlocation = currentLocation;
        map.setCenter(new google.maps.LatLng(currentLocation.latitude, currentLocation.longitude));
        if (map.getZoom() < maxZoom) {
          map.setZoom(maxZoom);
        }
      });

      this._markers.push(marker);
    }

    const clusterStyles = [
      {
        url: '../../../assets/svg/ic-map-cluster.svg',
        width: 32,
        height: 32,
        textColor: '#ffffff',
        textSize: 13,
      },
    ];

    const options = {
      gridSize: 36,
      styles: clusterStyles,
      maxZoom,
    };

    this.markerClusterer = new MarkerClusterer(map, this._markers, options);

    google.maps.event.addListener(infoWindow, 'closeclick', () => {
      this.setupMapMarkers(false);
    });

    if (resetZoom) {
      this._mapService.setMapZoom(map, this._locations, this.getLatLng(this.latitude, this.longitude));
    }
  }

  getLatLng(latitude: number, longitude: number): any {
    if (latitude && longitude) {
      return new google.maps.LatLng(latitude, longitude);
    }
    return null;
  }

  buildMapIcon(urlString: string, width: number, height: number, isLabelBlue: boolean, origin: any): MapIcon {
    const icon = {
      url: urlString,
      width,
      height,
      isLabelBlue,
      origin,
    };
    return icon;
  }

  getIconImage(icon: MapIcon): any {
    return {
      url: icon.url,
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(icon.width / 2, icon.height),
      labelOrigin: icon.origin,
      scaledSize: new google.maps.Size(icon.width, icon.height), // Scale down hi-res image for hi-res displays
    };
  }

  getIcon(location: WebApiModel.Location, isSelected: boolean): MapIcon {
    // Icons will be embedded into js by webpack because of 'require', they won't be copied to the webroot folder.
    let icon: MapIcon;
    let origin = new google.maps.Point(0, 0);
    const adjustLabel = isSelected ? 2 : 0;

    if (location.locationDetails.length > 1) {
      if (location.locationDetails.length > 19) {
        origin = new google.maps.Point(13 + adjustLabel, 14 + adjustLabel);
      } else if (location.locationDetails.length > 9) {
        origin = new google.maps.Point(14 + adjustLabel, 14 + adjustLabel);
      } else {
        origin = new google.maps.Point(15 + adjustLabel, 14 + adjustLabel);
      }

      if (isSelected) {
        icon = this.buildMapIcon('../../../assets/svg/ic-pin-green-cluster-selected.svg', 34, 48, false, origin);
      } else {
        icon = this.buildMapIcon('../../../assets/svg/ic-pin-green-cluster.svg', 29, 43, false, origin);
      }

      if (location.isAffiliatedProvider && location.isEasyClaim) {
        if (location.locationDetails.length > 9) {
          origin = new google.maps.Point(14 + adjustLabel * 1.5, 16 + adjustLabel);
        } else {
          origin = new google.maps.Point(15 + adjustLabel * 1.5, 15 + adjustLabel);
        }

        if (isSelected) {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-ec-and-ap-cluster-selected.svg', 41, 51, true, origin);
        } else {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-ec-and-ap-cluster.svg', 34, 45, true, origin);
        }
      } else if (location.isAffiliatedProvider) {
        if (isSelected) {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-blue-cluster-selected.svg', 34, 48, true, origin);
        } else {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-blue-cluster.svg', 29, 43, true, origin);
        }
      }
    } else {
      if (isSelected) {
        icon = this.buildMapIcon('../../../assets/svg/ic-pin-day-to-day-selected.svg', 34, 48, false, origin);
      } else {
        icon = this.buildMapIcon('../../../assets/svg/ic-pin-day-to-day.svg', 29, 43, false, origin);
      }

      const locationDetails = location.locationDetails[0];

      if (locationDetails.isAffiliatedProvider && locationDetails.isEasyClaim) {
        if (isSelected) {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-ec-and-ap-selected.svg', 41, 51, true, origin);
        } else {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-ec-and-ap.svg', 34, 45, true, origin);
        }
      } else if (locationDetails.isAffiliatedProvider) {
        if (isSelected) {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-affiliated-selected.svg', 34, 48, true, origin);
        } else {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-affiliated.svg', 29, 43, true, origin);
        }
      } else if (locationDetails.isEasyClaim) {
        if (isSelected) {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-easy-claim-selected.svg', 34, 48, false, origin);
        } else {
          icon = this.buildMapIcon('../../../assets/svg/ic-pin-easy-claim.svg', 29, 43, false, origin);
        }
      }
    }

    return icon;
  }

  buildInfoWindow(location: WebApiModel.Location): HTMLElement {
    const locationDetail = location.locationDetails[0];
    let address = '';

    const placeRoute: RouteUrl = RouterHelperService.getRoute(
      RouterHelperService.routeType.placeProfile,
      locationDetail.id,
      locationDetail.practiceName,
      null,
    );

    const personRoute: RouteUrl = RouterHelperService.getRoute(
      RouterHelperService.routeType.personProfile,
      locationDetail.personId,
      locationDetail.displayName,
      null,
    );

    // Create a wrapper container
    const infoContainer: HTMLElement = document.createElement('div');
    infoContainer.className = 'map-info-container';

    // Add practice link and AP|EC icons
    if (locationDetail.practiceName) {
      const practiceLink: HTMLAnchorElement = document.createElement('a');
      practiceLink.innerHTML = `<strong>${locationDetail.practiceName} </strong>`;
      practiceLink.href = placeRoute.link.join('/');
      practiceLink.className = 'marker-place-link';

      practiceLink.addEventListener('click', (event) => {
        this.linkClick(event, placeRoute);
      });
      if (locationDetail.isAffiliatedProvider) {
        const apStatus: HTMLElement = document.createElement('span');
        practiceLink.appendChild(apStatus);
      }

      if (locationDetail.isEasyClaim) {
        const ecStatus: HTMLElement = document.createElement('span');
        practiceLink.appendChild(ecStatus);
      }
      infoContainer.appendChild(practiceLink);
    }

    // Add person link, used on the search results map
    if (locationDetail.displayName) {
      const personLink: HTMLAnchorElement = document.createElement('a');
      const title = locationDetail.title ? locationDetail.title + ' ' : '';
      personLink.innerText = title + ' ' + locationDetail.displayName;
      personLink.href = personRoute.link.join('/');
      personLink.className = 'marker-person-link';
      personLink.addEventListener('click', (event) => {
        this.linkClick(event, personRoute);
      });
      infoContainer.appendChild(personLink);
    }

    const addressContainer: HTMLElement = document.createElement('div');

    address = this.buildString(address, locationDetail.receptionLocation);
    address = this.buildString(address, location.address);
    address = this.buildString(address, location.suburb);
    address = this.buildString(address, location.city);

    if (location.city && location.postcode) {
      address = address + ' ' + location.postcode;
    } else if (location.postcode) {
      address = this.buildString(address, location.postcode);
    }

    if (location.latitude && location.longitude) {
      const getDirections =
        '<a target="_blank" href=https://google.com/maps/dir/Current+Location/' +
        location.latitude +
        ',' +
        location.longitude +
        ' class="directions-link">Get directions</a>';
      address = this.buildString(address, getDirections);
    }

    addressContainer.innerHTML = address;

    infoContainer.appendChild(addressContainer);

    return infoContainer;
  }

  linkClick(event, routeLink: RouteUrl) {
    event.preventDefault();
    void this._router.navigate(routeLink.link);
  }

  /**
   * Helper to build a concatinated string and skip blank items
   */
  buildString(s: string, addressLine: string) {
    if (addressLine) {
      s = s ? s + '<br>' + addressLine : addressLine;
    }
    return s;
  }
}

interface MapIcon {
  url: string;
  width: number;
  height: number;
  isLabelBlue: boolean;
  origin: any;
}
